import React from "react";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { ShieldCheck, AlertTriangle, HelpCircle, Clock } from "lucide-react";
import { useLang } from "@/contexts/AppContexts";

const variantConfig = {
  REAL: {
    Icon: ShieldCheck,
    accent: "emerald",
    badgeClass:
      "bg-emerald-500/10 text-emerald-700 dark:text-emerald-200 border-emerald-500/30",
    barClass: "bg-emerald-500",
    ringClass: "ring-emerald-500/20",
  },
  AI: {
    Icon: AlertTriangle,
    accent: "red",
    badgeClass:
      "bg-red-500/10 text-red-700 dark:text-red-200 border-red-500/30",
    barClass: "bg-red-500",
    ringClass: "ring-red-500/20",
  },
  UNCERTAIN: {
    Icon: HelpCircle,
    accent: "amber",
    badgeClass:
      "bg-amber-500/10 text-amber-800 dark:text-amber-200 border-amber-500/30",
    barClass: "bg-amber-500",
    ringClass: "ring-amber-500/20",
  },
};

const formatDuration = (ms) => {
  if (!ms && ms !== 0) return "-";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const VerdictBadge = ({ verdict, confidence, regionsCount = 0, durationMs, c2paOverride, c2paModel }) => {
  const { t } = useLang();
  const cfg = variantConfig[verdict] || variantConfig.UNCERTAIN;
  const Icon = cfg.Icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.24, ease: "easeOut" }}
    >
      <Card
        className={`p-4 sm:p-5 ring-1 ${c2paOverride ? "ring-2 ring-red-500" : cfg.ringClass}`}
        data-testid="verdict-card"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border ${c2paOverride ? "bg-red-500/20 text-red-300 border-red-500/50" : cfg.badgeClass}`}
            >
              <Icon className="h-6 w-6" strokeWidth={2} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-mono-plex">
                {t("verdict.label")}
              </p>
              <p
                className="font-display text-xl sm:text-2xl font-semibold leading-tight"
                data-testid="verdict-badge"
              >
                {c2paOverride ? (
                  <>
                    {t(`verdict.${verdict}`)} —{" "}
                    <span className="text-red-600 dark:text-red-400">
                      🤖 C2PA Confirmed
                    </span>
                  </>
                ) : (
                  t(`verdict.${verdict}`)
                )}
              </p>
              {c2paOverride && c2paModel && (
                <p className="text-xs text-red-500 dark:text-red-400 mt-0.5 font-mono-plex">
                  Definitive: C2PA declares AI generator "{c2paModel}"
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-0.5">
                {regionsCount}{" "}
                {regionsCount === 1
                  ? t("verdict.regions_found")
                  : t("verdict.regions_found_plural")}
                {durationMs != null && (
                  <>
                    <span className="mx-1.5">·</span>
                    <Clock className="inline h-3 w-3 -mt-0.5 mr-0.5" />
                    {t("verdict.analyzed_in")} {formatDuration(durationMs)}
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:min-w-[260px]">
            <div className="flex-1">
              <p className="text-xs text-muted-foreground mb-1.5">
                {t("verdict.confidence")}
              </p>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full ${cfg.barClass} transition-[width]`}
                  style={{ width: `${Math.max(0, Math.min(100, confidence))}%` }}
                />
              </div>
            </div>
            <p
              className="font-display text-2xl font-bold tabular-nums"
              data-testid="confidence-percentage"
            >
              {confidence}
              <span className="text-base text-muted-foreground">%</span>
            </p>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export { VerdictBadge };
export default VerdictBadge;
